/**
 * @author Lorenzo Muscariello <lmuscariello@ciaopeople.com>
 */
import Semaphore from "semaphore-promise";
import { OuterResolvablePromise, PromiseOnSteroids } from "../../../lib/utils/PromiseOnSteroids.js";
import { VideoRef } from "../../player/MediaResource.js";
import { AdCallResolver, AdUnit, ResolvedAdUnit } from "../AdUnit.js";
import { AdContainerControls } from "../AdsController.js";
import { UserAdsData } from "../adcall/composable/UserAdsData.js";
import { PrebidModule } from "../prebid/PrebidModule.js";
import { AbstractImaController, ImaControllerConfig, WrappedImaAdsManager } from "./AbstractImaController.js";
import { IMAUnmuter } from "../../unmuter/IMAUserGestureUnmuter.js";
import { GDPRSdk } from "@ciaopeople/cp-shatsu/GDPR";


type QElement = {
    unit: AdUnit,
    resource: VideoRef,
    resolvedUnit: ResolvedAdUnit,
    resolvable: OuterResolvablePromise<WrappedImaAdsManager>
}





export abstract class AbstractQueuedImaController extends AbstractImaController {

  

    private queueSemaphore: Semaphore;
    private executionQueue: QElement[];
    private currentRequest: Promise<WrappedImaAdsManager>;


    protected constructor(ima: typeof google.ima,
        protected window: Window,
        protected gdpr: GDPRSdk,
        protected userAdData: UserAdsData,
        protected prebid: PrebidModule,
        protected imaControllerConfig: ImaControllerConfig,
        protected adResolver: AdCallResolver,
        protected adContainerControls: AdContainerControls,
        protected videoElement: HTMLVideoElement,
        imaUnmuter: IMAUnmuter
    ) {
       super(ima,window,gdpr,userAdData,prebid,imaControllerConfig,adResolver,adContainerControls,videoElement,imaUnmuter);

       this.queueSemaphore = new Semaphore();
       this.executionQueue = [];
       this.currentRequest = null;
    }
  

    protected executeRequest(unit: AdUnit, resource: VideoRef, resolvedAdUnit: ResolvedAdUnit): Promise<WrappedImaAdsManager> {
        this.logger.debug("Calling queued executeRequest");
        const out = this.queueSemaphore.acquire("doRequest").then(release => {
            const resolvable = PromiseOnSteroids.getResolvable<WrappedImaAdsManager>();
            this.executionQueue.push({
                unit: unit,
                resource: resource,
                resolvedUnit: resolvedAdUnit,
                resolvable: resolvable
            })
            release();
            return resolvable.promise;
        });
        this.dequeueIfPossible();
        return out;
    }



    private dequeueIfPossible(): void {

        this.queueSemaphore.acquire("dequeueRequest").then(release => {

            if(!this.currentRequest) {
                const extracted = this.executionQueue.shift();
                if(extracted) {
                    const theRequest = super.executeRequest(extracted.unit,extracted.resource,extracted.resolvedUnit);
                    this.currentRequest = theRequest;
                    extracted.resolvable.chainResolve(theRequest);
                    theRequest.finally(() =>  {
                        this.signalEnd();
                    });
                }
            }

            release();
        })
    }


    private signalEnd(): void {
        this.queueSemaphore.acquire("itemEnd").then(release => {
            this.currentRequest = null;
            release();
        }).then(_ => this.dequeueIfPossible());
    }

}